'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';

import { DEFAULT_SCREEN, OnboardingScreen, OnboardingView } from '../constants/user-onboarding-constants';

export type OnboardingData = {
	userName: string;
	workspaceName: string;
	workspacePhoto?: string;
};

type UserOnboardingContextType = {
	// State
	currentScreenIndex: number;
	currentScreen: OnboardingScreen;
	onboardingData: OnboardingData;
	source?: string; // source from where this component triggered
	skipLayout?: boolean; // skip the full-page screen layout wrapper

	// Actions
	goToNextScreen: () => void;
	goToPreviousScreen: () => void;
	goToScreen: (screen: OnboardingScreen) => void;

	// Data setters
	setUserName: (name: string) => void;
	setWorkspaceName: (name: string) => void;
	setWorkspacePhoto: (photo?: string) => void;

	// Validation
	isOnboardingComplete: () => boolean;

	// Completion
	completeOnboarding: () => void;

	// View change notification (for sub-views like join vs create within the same screen)
	notifyViewChange: (view: OnboardingView) => void;
};

const UserOnboardingContext = createContext<UserOnboardingContextType | undefined>(undefined);

type UserOnboardingProviderProps = {
	children: React.ReactNode;
	defaultScreen?: OnboardingScreen;
	allowedScreens?: OnboardingScreen[];
	onComplete?: (data: OnboardingData) => void;
	source?: string; // source from where this component triggered
	skipLayout?: boolean; // skip the full-page screen layout wrapper
	onScreenChange?: (screen: OnboardingView) => void;
};

export function UserOnboardingProvider({
	children,
	defaultScreen = DEFAULT_SCREEN,
	allowedScreens,
	onComplete,
	source,
	skipLayout = false,
	onScreenChange,
}: UserOnboardingProviderProps) {
	if (!allowedScreens) allowedScreens = Object.values(OnboardingScreen);

	const userDetails = useAppStore((state) => state.userDetails);
	const [currentScreenIndex, setCurrentScreenIndex] = useState(() => {
		const index = allowedScreens.indexOf(defaultScreen);
		return index >= 0 ? index : 0;
	});

	const [onboardingData, setOnboardingData] = useState<OnboardingData>({
		userName: userDetails?.firstName ? `${userDetails?.firstName} ${userDetails?.lastName}` : '',
		workspaceName: '',
		workspacePhoto: undefined,
	});

	const currentScreen = useMemo(() => {
		return allowedScreens[currentScreenIndex];
	}, [currentScreenIndex, allowedScreens]);

	const goToNextScreen = useCallback(() => {
		if (currentScreenIndex === allowedScreens.length - 1) return onComplete?.(onboardingData);

		setCurrentScreenIndex((prev) => Math.min(prev + 1, allowedScreens.length - 1));
	}, [currentScreenIndex, allowedScreens, onComplete, onboardingData]);

	const goToPreviousScreen = useCallback(() => {
		setCurrentScreenIndex((prev) => Math.max(prev - 1, 0));
	}, []);

	const goToScreen = useCallback((screen: OnboardingScreen) => {
		const index = allowedScreens.indexOf(screen);
		if (index >= 0) {
			setCurrentScreenIndex(index);
		}
	}, []);

	const setUserName = useCallback((name: string) => {
		setOnboardingData((prev) => ({ ...prev, userName: name }));
	}, []);

	const setWorkspaceName = useCallback((name: string) => {
		setOnboardingData((prev) => ({ ...prev, workspaceName: name }));
	}, []);

	const setWorkspacePhoto = useCallback((photo?: string) => {
		setOnboardingData((prev) => ({ ...prev, workspacePhoto: photo }));
	}, []);

	const isOnboardingComplete = useCallback(() => {
		return (
			onboardingData.userName.trim().length > 0 && onboardingData.workspaceName.trim().length > 0
		);
	}, [onboardingData]);

	const completeOnboarding = useCallback(() => {
		if (onComplete && isOnboardingComplete()) {
			onComplete(onboardingData);
		}
	}, [onComplete, isOnboardingComplete, onboardingData]);

	const notifyViewChange = useCallback(
		(view: OnboardingView) => {
			onScreenChange?.(view);
		},
		[onScreenChange]
	);

	useEffect(() => {
		onScreenChange?.(currentScreen);
	}, [currentScreen, onScreenChange]);

	const contextValue = useMemo(
		() => ({
			currentScreenIndex,
			currentScreen,
			onboardingData,
			goToNextScreen,
			goToPreviousScreen,
			goToScreen,
			setUserName,
			setWorkspaceName,
			setWorkspacePhoto,
			isOnboardingComplete,
			completeOnboarding,
			source,
			skipLayout,
			notifyViewChange,
		}),
		[
			currentScreenIndex,
			currentScreen,
			onboardingData,
			goToNextScreen,
			goToPreviousScreen,
			goToScreen,
			setUserName,
			setWorkspaceName,
			setWorkspacePhoto,
			isOnboardingComplete,
			completeOnboarding,
			source,
			skipLayout,
			notifyViewChange,
		]
	);

	return (
		<UserOnboardingContext.Provider value={contextValue}>{children}</UserOnboardingContext.Provider>
	);
}

export function useUserOnboarding() {
	const context = useContext(UserOnboardingContext);
	if (context === undefined) {
		throw new Error('useUserOnboarding must be used within a UserOnboardingProvider');
	}
	return context;
}

export function useUserOnboardingSafe() {
	return useContext(UserOnboardingContext) ?? null;
}
